import { createWebSocketRequest } from './utils';

/**
 * Get all markets data
 * @param {string} category - Optional category to filter markets
 * @returns {Promise<Array>} A promise that resolves with the markets data
 */
export const getMarkets = (category = null) => {
  return createWebSocketRequest({
    eventName: 'markets-data',
    requestType: 'get-markets',
    requestData: category ? { category } : undefined,
    dataValidator: (data) => {
      if (data && data.markets && Array.isArray(data.markets)) {
        return data.markets.map(market => ({
          id: market.symbol || market.id,
          name: market.displayName || market.name || market.symbol,
          price: market.price || market.lastPrice || 0,
          change: market.change24h || market.priceChange || 0,
          category: market.category || market.marketType || 'unknown',
          volume: market.volume24h || market.volume || 'N/A'
        }));
      }
      return [];
    },
    errorMessage: 'Failed to fetch market data'
  });
};

/**
 * Get total number of instruments
 * @param {string} category - Optional category to filter instruments
 * @returns {Promise<number>} A promise that resolves with the total number of instruments
 */
export const getInstrumentsLength = (category = null) => {
  return createWebSocketRequest({
    eventName: 'instruments-length-response',
    requestType: 'get-instruments-length',
    requestData: category ? { category } : undefined,
    dataValidator: (data) => {
      console.log(`Instrument length response:`, data);
      
      // Handle direct number in data property - exact format from logs
      // {"type":"instruments-length-response","data":36}
      if (data && typeof data.data === 'number') {
        console.log(`Found instrument length in data as number: ${data.data}`);
        return data.data;
      }
      
      // Handle object with data array format
      if (data && Array.isArray(data.data)) {
        console.log(`Found instrument length in data array: ${data.data.length}`);
        return data.data.length;
      }
      
      // Handle count property
      if (data && typeof data.count === 'number') {
        console.log(`Found instrument length in count property: ${data.count}`);
        return data.count;
      }
      
      // Handle length property
      if (data && typeof data.length === 'number') {
        console.log(`Found instrument length in length property: ${data.length}`);
        return data.length;
      }
      
      // Handle direct array
      if (Array.isArray(data)) {
        console.log(`Found instrument length as array length: ${data.length}`);
        return data.length;
      }
      
      // Handle numeric response
      if (typeof data === 'number') {
        console.log(`Found instrument length as direct number: ${data}`);
        return data;
      }
      
      // Log if we can't determine format
      console.log(`Could not determine instrument length from response:`, data);
      
      // Return at least 1 to show something to the user
      return 1;
    },
    errorMessage: 'Failed to fetch instruments length'
  });
};

/**
 * Get instrument data by index
 * @param {number} index - The index of the instrument to fetch
 * @param {string} category - Optional category to filter instruments
 * @returns {Promise<Object>} A promise that resolves with the instrument data
 */
export const getInstrumentByIndex = (index, category = null) => {
  return createWebSocketRequest({
    eventName: 'get-instruments-by-index-response',
    requestType: 'get-ins-by-index',
    requestData: { 
      index, 
      ...(category ? { category } : {}) 
    },
    dataValidator: (data) => {
      // The response format is { type: 'get-instruments-by-index-response', data: Array(10) }
      if (data && Array.isArray(data.data)) {
        return formatInstrument(data.data[index] || null);
      }
      
      // If no valid data found, create a placeholder
      return {
        id: `instrument-${index}`,
        name: `Instrument #${index}`,
        price: 0,
        change: 0,
        category: category || 'forex',
        volume: 'N/A'
      };
    },
    errorMessage: `Failed to fetch instrument data at index ${index}`
  });
};

/**
 * Helper function to format instrument data consistently
 */
function formatInstrument(instrument) {
  if (!instrument) return null;
  
  return {
    id: instrument.symbol || `instrument-${Date.now()}`,
    name: instrument.description || instrument.symbol || `Instrument`,
    price: parseNumeric(instrument.price) || 0,
    change: parseNumeric(instrument['24hour-change']) || 0,
    category: 'forex',
    volume: 'N/A'
  };
}

/**
 * Parse a value as a number, handling different formats
 */
function parseNumeric(value) {
  if (value === undefined || value === null) {
    return 0;
  }
  
  if (typeof value === 'number') {
    return value;
  }
  
  if (typeof value === 'string') {
    // Remove any commas, currency symbols, etc.
    const cleaned = value.replace(/[^\d.-]/g, '');
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  }
  
  return 0;
} 